import { useCallback, useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Target, Users2, Search, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { inr, monthLabel, currentMonth } from "../lib/format";
import { PageHeader, ConfirmDialog, inputCls } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

export default function Teams() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [del, setDel] = useState(null);
  const month = currentMonth();

  // Create / Edit Team Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [leaderCode, setLeaderCode] = useState("");
  const [monthlyTarget, setMonthlyTarget] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get(`/teams?month=${month}`).then((r) => setTeams(r.data));
    if (isAdmin) api.get("/agents").then((r) => setAgents(r.data));
  }, [isAdmin, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenCreate = () => {
    setEditingTeam(null);
    setTeamName("");
    setLeaderCode("");
    setMonthlyTarget("");
    setSelectedMembers([]);
    setSearchQuery("");
    setModalOpen(true);
  };

  const handleOpenEdit = (team) => {
    setEditingTeam(team);
    setTeamName(team.name || "");
    setLeaderCode(team.leader_code || "");
    setMonthlyTarget(team.monthly_target || "");
    const initialMembers = team.member_codes || team.members?.map((m) => m.agent_code) || [];
    setSelectedMembers(initialMembers);
    setSearchQuery("");
    setModalOpen(true);
  };

  const handleLeaderChange = (newLeaderCode) => {
    setLeaderCode(newLeaderCode);
    if (newLeaderCode && !selectedMembers.includes(newLeaderCode)) {
      setSelectedMembers((prev) => [...prev, newLeaderCode]);
    }
  };

  const toggleMember = (code) => {
    setSelectedMembers((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    const q = searchQuery.toLowerCase();
    return agents.filter(
      (a) =>
        a.name?.toLowerCase().includes(q) ||
        a.agent_code?.toLowerCase().includes(q) ||
        a.designation?.toLowerCase().includes(q)
    );
  }, [agents, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      name: teamName.trim(),
      leader_code: leaderCode || "",
      monthly_target: parseFloat(monthlyTarget) || 0,
      member_codes: selectedMembers,
    };

    try {
      if (editingTeam?.id) {
        await api.put(`/teams/${editingTeam.id}`, payload);
        toast.success("Team updated successfully");
      } else {
        await api.post("/teams", payload);
        toast.success("Team created successfully");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="teams-page">
      <PageHeader title="Teams" sub={`Team performance for ${monthLabel(month)}`} testid="teams-header">
        {isAdmin && (
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={handleOpenCreate} data-testid="add-team-button">
            <Plus className="mr-1 h-4 w-4" /> Create Team
          </Button>
        )}
      </PageHeader>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {teams.map((t) => (
          <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={`team-card-${t.name.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{t.name}</h3>
                <p className="text-xs text-slate-500">Leader: {t.leader_name || "-"}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" aria-label="Edit team" data-testid={`edit-team-${t.id}`} onClick={() => handleOpenEdit(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" aria-label="Delete team" className="text-red-600" data-testid={`delete-team-${t.id}`} onClick={() => setDel(t)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {t.members?.map((m) => (
                <span key={m.agent_code} className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                  {m.name}
                </span>
              ))}
              {(!t.members || t.members.length === 0) && <span className="text-xs text-slate-400">No members</span>}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Team Sales</p><p className="font-num font-bold text-slate-900">{inr(t.team_sales)}</p></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Bookings</p><p className="font-num font-bold text-slate-900">{t.team_bookings}</p></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Collection</p><p className="font-num font-bold text-slate-900">{inr(t.team_collection)}</p></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Commission</p><p className="font-num font-bold text-slate-900">{inr(t.team_commission)}</p></div>
              {t.team_expenses !== undefined && (
                <div className="col-span-2"><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Team Expenses (this month)</p><p className="font-num font-bold text-red-700" data-testid={`team-expenses-${t.id}`}>{inr(t.team_expenses || 0)}</p></div>
              )}
            </div>
            <div className="mt-4 rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 font-semibold text-slate-600"><Target className="h-3.5 w-3.5" /> Monthly Target</span>
                <span className="font-num font-semibold text-slate-900">{inr(t.monthly_target)}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className={`h-full ${t.achievement >= 100 ? "bg-emerald-600" : "bg-amber-500"}`} style={{ width: `${Math.min(100, t.achievement)}%` }} />
              </div>
              <p className="mt-1 text-right text-xs font-num font-semibold text-slate-600">{t.achievement}% achieved</p>
            </div>
          </div>
        ))}
      </div>
      {teams.length === 0 && <p className="py-16 text-center text-sm text-slate-400">No teams yet</p>}

      {/* Enhanced Create / Edit Team Modal with Team Members Adding Section */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="team-form">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingTeam ? "Edit Team" : "Create Team"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Team Name */}
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Team Name (Optional)
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g. Falcons, Titans (or leave blank for auto-name)"
                  data-testid="team-form-field-name"
                />
              </div>

              {/* Team Leader */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Team Leader
                </Label>
                <select
                  className={`${inputCls} mt-1.5`}
                  value={leaderCode}
                  onChange={(e) => handleLeaderChange(e.target.value)}
                  data-testid="team-form-field-leader_code"
                >
                  <option value="">Select Team Leader...</option>
                  {agents.map((a) => (
                    <option key={a.agent_code} value={a.agent_code}>
                      {a.name} ({a.agent_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Monthly Target */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Monthly Target (₹)
                </Label>
                <Input
                  type="number"
                  className="mt-1.5"
                  value={monthlyTarget}
                  onChange={(e) => setMonthlyTarget(e.target.value)}
                  placeholder="e.g. 5000000"
                  data-testid="team-form-field-monthly_target"
                />
              </div>
            </div>

            {/* Team Members Adding Section */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users2 className="h-4 w-4 text-emerald-600" />
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Team Members Adding Section
                  </Label>
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                    {selectedMembers.length} selected
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setSelectedMembers(agents.map((a) => a.agent_code))}
                    className="font-semibold text-emerald-700 hover:text-emerald-800 text-xs"
                  >
                    Select All
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={() => setSelectedMembers(leaderCode ? [leaderCode] : [])}
                    className="text-slate-500 hover:text-slate-700 text-xs"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Search agents */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search staff/agents by name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 text-xs h-8 bg-white"
                />
              </div>

              {/* Agents selection list */}
              <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                {filteredAgents.map((a) => {
                  const isSelected = selectedMembers.includes(a.agent_code);
                  const isLeader = leaderCode === a.agent_code;
                  return (
                    <div
                      key={a.agent_code}
                      onClick={() => toggleMember(a.agent_code)}
                      className={`flex items-center justify-between p-2.5 cursor-pointer transition-colors text-xs ${
                        isSelected ? "bg-emerald-50/70" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 pointer-events-none"
                        />
                        <div>
                          <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                            {a.name}
                            {isLeader && (
                              <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                👑 Leader
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 font-mono">
                            <span>{a.agent_code}</span>
                            {a.designation && (
                              <span className="font-sans text-slate-400">• {a.designation}</span>
                            )}
                            {a.team_name && a.team_name !== editingTeam?.name && (
                              <span className="text-amber-700 font-sans italic">
                                (Currently in {a.team_name})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {isSelected ? (
                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded flex items-center gap-1">
                          <Check className="h-3 w-3" /> Member
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400 hover:text-slate-600">
                          + Add
                        </span>
                      )}
                    </div>
                  );
                })}
                {filteredAgents.length === 0 && (
                  <div className="p-4 text-center text-xs text-slate-400">
                    No agents found matching &quot;{searchQuery}&quot;
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="mt-4 gap-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800">
                {submitting ? "Saving..." : editingTeam ? "Save Changes" : "Create Team"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!del}
        onOpenChange={() => setDel(null)}
        title="Delete Team"
        message={`Delete team ${del?.name}? Members will be unassigned.`}
        onConfirm={async () => {
          await api.delete(`/teams/${del.id}`);
          toast.success("Team deleted");
          load();
        }}
        testid="team-delete-confirm"
      />
    </div>
  );
}
